import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../../common/apiClient'
import type { AppItem } from './model'
import { APP_CATALOGUE_STALE_TIME, appCatalogueAccessDenied, appCatalogueScopeKey, clearAppCatalogue, readAppCatalogue, writeAppCatalogue, type AppCatalogueKind, type AppCatalogueScope } from './appCatalogueCache'

export function useCachedAppCatalogue(scope: AppCatalogueScope | null, kind: AppCatalogueKind) {
  const enabled = Boolean(scope && (kind !== 'team-apps' || scope.team))
  const query = useQuery({
    queryKey: ['home-preview', kind, scope ? appCatalogueScopeKey(scope) : null],
    enabled,
    initialData: () => enabled ? readAppCatalogue(scope!, kind) : undefined,
    // A restored browser snapshot is shown immediately but verified on reload.
    // Subsequent in-app visits reuse the fresh in-memory response for five minutes.
    initialDataUpdatedAt: 0,
    staleTime: APP_CATALOGUE_STALE_TIME,
    gcTime: 30 * 60 * 1000,
    retry: (failures, error) => !appCatalogueAccessDenied(error) && failures < 1,
    queryFn: async ({ signal }) => {
      const url = kind === 'team-apps' ? `/api/team-apps/team/${encodeURIComponent(scope!.team)}` : '/api/company-services'
      try {
        const data = await apiClient.get<AppItem[]>(url, { signal })
        if (!Array.isArray(data)) throw new Error('App catalogue unavailable')
        if (!signal.aborted) writeAppCatalogue(scope!, kind, data)
        return data
      } catch (error) {
        if (appCatalogueAccessDenied(error)) clearAppCatalogue(scope!, kind)
        throw error
      }
    },
  })
  // A denied refresh must never leave previously cached links visible.
  return { ...query, data: appCatalogueAccessDenied(query.error) ? undefined : query.data }
}
